<script lang="ts" setup>
import CategoriesField from '@/components/categories/CategoriesField.vue';
import CategoryField from '@/components/categories/CategoryField.vue';
import AddressField from '@/components/common/AddressField.vue';
import ContactField from '@/components/common/ContactField.vue';
import RichTextField from '@/components/common/RichTextField.vue';
import MediaList from '@/components/media/MediaList.vue';
import InitiativeState from '@/components/initiative/InitiativeState.vue';
import { Contact } from '@/models/contact.model';
import {
  impactAreaItems,
  Initiative,
  defaultInitiative,
  InitiativeStatus,
} from '@/models/initiative.model';
import { MediaPurpose } from '@/models/media.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { User, UserLevel } from '@/models/user.model';
import HelpDialog from '@/components/common/HelpDialog.vue';

const route = useRoute();
const store = useStore<ShiftCMSState & { cns: ContentStoreState }>();
const events = useEvents();
const uuid = route.params.uuid as string;
const { t } = useI18n();

const user = computed<User | null>(() => store.state.loggedInUser);

const value = ref<Initiative>(defaultInitiative);
let currentValue: Initiative = { ...defaultInitiative };
const valid = ref<boolean>(true);

const emit = defineEmits([
  'update:modelValue',
  'update:published',
  'remove-contact',
]);

let translatedAreaItems = impactAreaItems.map((i) => ({
  title: t(`initiative.edit.area_${i.value}`),
  value: i.value,
}));

const update = () => {
  emit('update:modelValue', value.value);
};

const removeContact = (item: Contact) => {
  emit('remove-contact', item);
};

const updateLanguage = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.GetInitiativeByUUID,
    uuid,
  );
  currentValue = { ...initiative }; // store the current value
  value.value = { ...initiative };
  translatedAreaItems = impactAreaItems.map((i) => ({
    title: t(`initiative.edit.area_${i.value}`),
    value: i.value,
  }));
};

const save = async () => {
  if (!valid.value) {
    return;
  }

  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeByUUID,
    { uuid, initiative: value.value },
  );
  if (initiative?.id) {
    currentValue = { ...initiative }; // store the current value
    value.value = { ...initiative };
  }
};

const setReady = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeStatusByUUID,
    {
      uuid,
      initiative: { ...value.value, status: InitiativeStatus.ReadyForReview },
    },
  );
  if (initiative?.id) {
    value.value = { ...initiative };
  }
};

const setReadyForPublishing = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeStatusByUUID,
    {
      uuid,
      initiative: {
        ...value.value,
        status: InitiativeStatus.ReadyForPublishing,
      },
    },
  );
  if (initiative?.id) {
    value.value = { ...initiative };
  }
};

const setBackToEditing = async () => {
  const initiative: Initiative = await store.dispatch(
    ContentStoreActions.UpdateInitiativeStatusByUUID,
    {
      uuid,
      initiative: {
        ...value.value,
        status: InitiativeStatus.Editing,
      },
    },
  );
  if (initiative?.id) {
    value.value = { ...initiative };
  }
};

function share() {
  navigator.clipboard.writeText(
    `${document.location.protocol}//${document.location.host}/#/initiative/${value.value.publicUUID}/edit`,
  );
  store.dispatch(Actions.ShowNotification, {
    message: 'Copied to clipboard',
    type: 'success',
  });
}

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  updateLanguage();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

const disabled = computed(
  () =>
    (value.value.status !== InitiativeStatus.Editing && !user.value) ||
    (![InitiativeStatus.Editing, InitiativeStatus.ReadyForReview].includes(
      value.value.status,
    ) &&
      user.value?.level === UserLevel.ContentEditor),
);

const rules = {
  required: (value: string) => !!value || t('initiative.edit.required'),
};

function getSimpleValue(i: Initiative) {
  return {
    title: i.title,
    quizTitle: i.quizTitle,
    description: i.description,
    shortDescription: i.shortDescription,
    website: i.website,
    area: i.impactArea,
    categories: i.categories.map((c) => c.id),
    address: i.address,
    media: i.media.map((c) => c.id),
  };
}

const isDirty = computed<boolean>(() => {
  const isDirty =
    JSON.stringify(getSimpleValue(value.value)) !==
    JSON.stringify(getSimpleValue(currentValue));

  store.commit(Mutations.SetDirtyState, isDirty);

  return isDirty;
});
</script>

<template>
  <v-container>
    <v-form v-model="valid" @submit.prevent="save">
      <v-row>
        <v-col cols="9">
          <h1>
            {{ $t('initiative.edit.header') }} <span v-if="isDirty">*</span>
          </h1>

          <v-text-field
            v-model="value.title"
            @change="update"
            :label="$t('initiative.edit.name')"
            :hint="$t('initiative.edit.nameHint')"
            required
            :disabled="disabled"
            :rules="[rules.required]"
          ></v-text-field>

          <v-timeline
            direction="horizontal"
            density="compact"
            :fill-dot="false"
            size="x-small"
            align="start"
          >
            <v-timeline-item
              :dot-color="
                value.status === InitiativeStatus.Editing
                  ? 'primary'
                  : '#efefef'
              "
            >
              <v-chip
                variant="flat"
                density="compact"
                :color="
                  value.status === InitiativeStatus.Editing
                    ? 'primary'
                    : '#808080'
                "
                >{{ $t('initiative.edit.status_editing') }}</v-chip
              >
            </v-timeline-item>
            <v-timeline-item
              :dot-color="
                value.status === InitiativeStatus.ReadyForReview
                  ? 'primary'
                  : '#efefef'
              "
            >
              <v-chip
                variant="flat"
                density="compact"
                :color="
                  value.status === InitiativeStatus.ReadyForReview
                    ? 'primary'
                    : '#808080'
                "
                >{{ $t('initiative.edit.status_review_long') }}</v-chip
              >
            </v-timeline-item>
            <v-timeline-item
              :dot-color="
                value.status === InitiativeStatus.ReadyForPublishing
                  ? 'primary'
                  : '#efefef'
              "
            >
              <v-chip
                variant="flat"
                density="compact"
                :color="
                  value.status === InitiativeStatus.ReadyForPublishing
                    ? 'primary'
                    : '#808080'
                "
                >{{ $t('initiative.edit.status_publishing_long') }}</v-chip
              >
            </v-timeline-item>
            <v-timeline-item
              :dot-color="
                value.status === InitiativeStatus.Published
                  ? 'primary'
                  : '#efefef'
              "
            >
              <v-chip
                variant="flat"
                density="compact"
                :color="
                  value.status === InitiativeStatus.Published
                    ? 'primary'
                    : '#808080'
                "
                >{{ $t('initiative.edit.status_published') }}</v-chip
              >
            </v-timeline-item>
          </v-timeline>

          <v-text-field
            v-model="value.quizTitle"
            @change="update"
            :label="$t('initiative.edit.quizTitle')"
            :hint="$t('initiative.edit.quizTitleHint')"
            required
            counter
            :disabled="disabled"
            :rules="[rules.required]"
            class="mt-4"
          ></v-text-field>

          <v-text-field
            :label="$t('initiative.edit.website')"
            :hint="$t('initiative.edit.websiteHint')"
            v-model="value.website"
            @change="update"
            :disabled="disabled"
            placeholder="https://"
            :rules="[rules.required]"
            required
          ></v-text-field>

          <v-row>
            <v-col>
              <RichTextField
                :label="$t('initiative.edit.shortDescription')"
                :hint="$t('initiative.edit.shortDescriptionHint')"
                :height="200"
                v-model="value.shortDescription"
                @change="update"
                :disabled="disabled"
                :media="value.media"
                :rules="[rules.required]"
              />
              <RichTextField
                :label="$t('initiative.edit.description')"
                :hint="$t('initiative.edit.descriptionHint')"
                v-model="value.description"
                @change="update"
                :disabled="disabled"
                :media="value.media"
                :rules="[rules.required]"
              />
            </v-col>
            <v-col cols="1">
              <HelpDialog>
                <h2>{{ $t('initiative.help.descriptionHeader') }}</h2>
                <p class="mt-4">{{ $t('initiative.help.descriptionText1') }}</p>
              </HelpDialog>
            </v-col>
          </v-row>

          <v-select
            v-model="value.impactArea"
            :items="translatedAreaItems"
            @update:model-value="update"
            :label="$t('initiative.edit.area')"
            :hint="$t('initiative.edit.areaHint')"
            required
            @change="update"
            :disabled="disabled"
            :rules="[rules.required]"
          ></v-select>

          <v-row>
            <v-col>
              <CategoryField
                v-if="value.mainCategory"
                main
                v-model="value.mainCategory"
                @change="update"
                disabled
                :label="$t('initiative.edit.mainCategory')"
              />
              <CategoriesField
                v-model="value.categories"
                @change="update"
                :disabled="disabled"
              />
            </v-col>
            <v-col cols="1">
              <HelpDialog>
                <h2>{{ $t('initiative.help.categoryHeader') }}</h2>
                <p class="mt-4">{{ $t('initiative.help.categoryText1') }}</p>
                <p class="mt-4">{{ $t('initiative.help.categoryText2') }}</p>
              </HelpDialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <AddressField
                v-model="value.address"
                @change="update"
                :disabled="disabled"
              />
            </v-col>
            <v-col cols="1">
              <HelpDialog>
                <h2>{{ $t('initiative.help.addressHeader') }}</h2>
                <p class="mt-4">{{ $t('initiative.help.addressText1') }}</p>
                <p class="mt-4">{{ $t('initiative.help.addressText2') }}</p>
              </HelpDialog>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <ContactField
                v-model="value.contacts"
                v-if="value.contacts"
                @change="update"
                @remove="removeContact"
                :disabled="disabled"
              />
            </v-col>
            <v-col cols="1">
              <HelpDialog>
                <h2>{{ $t('initiative.help.contactHeader') }}</h2>
                <p class="mt-4">{{ $t('initiative.help.contactText1') }}</p>
              </HelpDialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <MediaList
                v-model="value"
                @change="update"
                top
                :disabled="disabled"
                :required="[MediaPurpose.BackgroundImage, MediaPurpose.Main]"
              />
            </v-col>
            <v-col cols="1">
              <HelpDialog>
                <h2>{{ $t('initiative.help.mediaHeader') }}</h2>
                <p class="mt-4">{{ $t('initiative.help.mediaText1') }}</p>
                <p class="mt-4">{{ $t('initiative.help.mediaText2') }}</p>
                <p class="mt-4">{{ $t('initiative.help.mediaText3') }}</p>
              </HelpDialog>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="3">
          <InitiativeState
            :translation="value.translated"
            :media="value.media"
          />
          <v-card class="mt-8">
            <v-card-text>
              <v-btn type="submit" color="primary" block :disabled="disabled">{{
                $t('initiative.edit.save')
              }}</v-btn>
              <v-btn
                class="mt-4"
                @click="setReady"
                color="success"
                block
                v-if="value.status === InitiativeStatus.Editing"
                >{{ $t('initiative.edit.readyForReview') }}</v-btn
              >
              <v-btn
                class="mt-4"
                @click="setReadyForPublishing"
                color="success"
                block
                v-if="
                  value.status === InitiativeStatus.ReadyForReview &&
                  user?.level === UserLevel.ContentEditor
                "
                >{{ $t('initiative.edit.readyForPublishing') }}</v-btn
              >
              <v-btn
                class="mt-4"
                @click="setBackToEditing"
                color="info"
                block
                v-if="
                  value.status === InitiativeStatus.ReadyForReview &&
                  user?.level === UserLevel.ContentEditor
                "
                >{{ $t('initiative.edit.editing') }}</v-btn
              >
              <v-btn
                class="mt-4"
                @click="share"
                color="indigo-lighten-3
"
                inverted
                block
                v-if="user?.level === UserLevel.ContentEditor"
                >{{ $t('initiative.edit.share') }}</v-btn
              >
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>
